import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Build as BuildIcon,
  Campaign as CampaignIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useNavigate } from 'react-router-dom';
import { marketingApi } from '../../api/marketing';
import type { MarketAudience } from '../../api/marketing';

interface AudienceFilter {
  source?: string[];
  created_at?: {
    gte?: string;
    lte?: string;
  };
  is_valid?: boolean;
}

const AudiencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<MarketAudience[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState<MarketAudience | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewMembers, setPreviewMembers] = useState<any[]>([]);
  const [rebuilding, setRebuilding] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [audienceToDelete, setAudienceToDelete] = useState<MarketAudience | null>(null);
  const [campaignsDialogOpen, setCampaignsDialogOpen] = useState(false);
  const [audienceCampaigns, setAudienceCampaigns] = useState<any[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // Create/Edit form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    filter: {
      source: [] as string[],
      created_at: {
        gte: null as Date | null,
        lte: null as Date | null,
      },
      is_valid: null as boolean | null,
    },
  });

  const sources = ['csv', 'sqlflow', 'manual', 'api', 'signup', 'website', 'client', 'lead_magnet'];

  useEffect(() => {
    fetchAudiences();
  }, []);

  const fetchAudiences = async () => {
    try {
      setLoading(true);
      const response = await marketingApi.getAudiences({ is_active: true });
      setAudiences(response.results);
    } catch (error) {
      console.error('Failed to fetch audiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAudience = async () => {
    try {
      const filter_json: AudienceFilter = {};

      if (formData.filter.source.length > 0) {
        filter_json.source = formData.filter.source;
      }

      if (formData.filter.created_at.gte || formData.filter.created_at.lte) {
        filter_json.created_at = {};
        if (formData.filter.created_at.gte) {
          filter_json.created_at.gte = formData.filter.created_at.gte.toISOString().split('T')[0];
        }
        if (formData.filter.created_at.lte) {
          filter_json.created_at.lte = formData.filter.created_at.lte.toISOString().split('T')[0];
        }
      }

      if (formData.filter.is_valid !== null) {
        filter_json.is_valid = formData.filter.is_valid;
      }

      const response = await marketingApi.createAudience({
        name: formData.name,
        description: formData.description,
        filter_json,
      });

      // Rebuild members after creation
      await marketingApi.rebuildAudience(response.id);

      setCreateDialogOpen(false);
      resetForm();
      await fetchAudiences();
    } catch (error) {
      console.error('Failed to create audience:', error);
    }
  };

  const handleRebuildAudience = async (audienceId: number) => {
    try {
      setRebuilding(audienceId);
      const response = await marketingApi.rebuildAudience(audienceId, true);
      await fetchAudiences();
    } catch (error) {
      console.error('Failed to rebuild audience:', error);
    } finally {
      setRebuilding(null);
    }
  };

  const handlePreviewMembers = async (audience: MarketAudience) => {
    try {
      const response = await marketingApi.getAudienceMembers(audience.id, 1);
      setPreviewMembers(response.results);
      setSelectedAudience(audience);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleDeleteClick = (audience: MarketAudience) => {
    setAudienceToDelete(audience);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!audienceToDelete) return;

    try {
      await marketingApi.deleteAudience(audienceToDelete.id);
      setDeleteDialogOpen(false);
      setAudienceToDelete(null);
      await fetchAudiences();
    } catch (error: any) {
      console.error('Failed to delete audience:', error);
      alert(error.response?.data?.error || 'Failed to delete audience');
    }
  };

  const handleViewCampaigns = async (audience: MarketAudience) => {
    try {
      setLoadingCampaigns(true);
      setSelectedAudience(audience);
      setCampaignsDialogOpen(true);
      const response = await marketingApi.getCampaigns({ audience: audience.id });
      setAudienceCampaigns(response.results);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      alert('Failed to load campaigns for this audience');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      filter: {
        source: [],
        created_at: {
          gte: null,
          lte: null,
        },
        is_valid: null,
      },
    });
  };

  const getFilterDescription = (filter_json: any) => {
    if (!filter_json) return 'All contacts';

    const parts = [];
    if (filter_json.source?.length > 0) {
      parts.push(`Sources: ${filter_json.source.join(', ')}`);
    }
    if (filter_json.created_at?.gte || filter_json.created_at?.lte) {
      const dates = [];
      if (filter_json.created_at.gte) dates.push(`from ${filter_json.created_at.gte}`);
      if (filter_json.created_at.lte) dates.push(`to ${filter_json.created_at.lte}`);
      parts.push(`Date: ${dates.join(' ')}`);
    }
    if (filter_json.is_valid !== undefined) {
      parts.push(filter_json.is_valid ? 'Valid emails only' : 'Invalid emails only');
    }

    return parts.length > 0 ? parts.join(' | ') : 'All contacts';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Audiences
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Audience
          </Button>
        </Box>

        {/* Audiences Grid */}
        {loading ? (
          <LinearProgress />
        ) : audiences.length > 0 ? (
          <Grid container spacing={3}>
            {audiences.map((audience) => (
              <Grid item xs={12} md={6} lg={4} key={audience.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Typography variant="h6">{audience.name}</Typography>
                      {audience.needs_rebuild && (
                        <Chip label="Needs Rebuild" color="warning" size="small" />
                      )}
                    </Box>

                    <Typography variant="body2" color="textSecondary" paragraph>
                      {audience.description || 'No description'}
                    </Typography>

                    <Box mb={2}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Filter: {getFilterDescription(audience.filter_json)}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={2} mb={2}>
                      <Chip
                        icon={<PeopleIcon />}
                        label={`${audience.member_count || 0} members`}
                        color="primary"
                        variant="outlined"
                      />
                      {audience.eligible_count !== undefined && audience.eligible_count < audience.member_count && (
                        <Chip
                          label={`${audience.eligible_count} eligible`}
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {audience.last_rebuilt && (
                      <Typography variant="caption" color="textSecondary">
                        Last rebuilt: {new Date(audience.last_rebuilt).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handlePreviewMembers(audience)}
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      startIcon={<BuildIcon />}
                      onClick={() => handleRebuildAudience(audience.id)}
                      disabled={rebuilding === audience.id}
                    >
                      {rebuilding === audience.id ? 'Rebuilding...' : 'Rebuild'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CampaignIcon />}
                      onClick={() => handleViewCampaigns(audience)}
                    >
                      {audience.campaign_count > 0
                        ? `${audience.campaign_count} Campaign${audience.campaign_count !== 1 ? 's' : ''}`
                        : 'Campaigns'}
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/marketing/campaigns/create?audience=${audience.id}`)}
                    >
                      New Campaign
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(audience)}
                      disabled={!audience.is_deletable}
                      title={
                        audience.is_deletable
                          ? 'Delete this audience'
                          : `Cannot delete: used by ${audience.campaign_count} campaign(s)`
                      }
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              No audiences created yet
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Create an audience to segment your contacts for targeted campaigns
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Your First Audience
            </Button>
          </Paper>
        )}

        {/* Create Audience Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create Audience</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Audience Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={2}
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                Filter Criteria
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="source-filter-label">Source</InputLabel>
                    <Select
                      labelId="source-filter-label"
                      id="source-filter"
                      multiple
                      label="Source"
                      value={formData.filter.source}
                      onChange={(e) => setFormData({
                        ...formData,
                        filter: { ...formData.filter, source: e.target.value as string[] }
                      })}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {sources.map((source) => (
                        <MenuItem key={source} value={source}>
                          <Checkbox checked={formData.filter.source.indexOf(source) > -1} />
                          <ListItemText primary={source} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Created After"
                    value={formData.filter.created_at.gte}
                    onChange={(date) => setFormData({
                      ...formData,
                      filter: {
                        ...formData.filter,
                        created_at: { ...formData.filter.created_at, gte: date }
                      }
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Created Before"
                    value={formData.filter.created_at.lte}
                    onChange={(date) => setFormData({
                      ...formData,
                      filter: {
                        ...formData.filter,
                        created_at: { ...formData.filter.created_at, lte: date }
                      }
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="email-validity-label">Email Validity</InputLabel>
                    <Select
                      labelId="email-validity-label"
                      id="email-validity"
                      label="Email Validity"
                      value={formData.filter.is_valid === null ? '' : formData.filter.is_valid.toString()}
                      onChange={(e) => setFormData({
                        ...formData,
                        filter: {
                          ...formData.filter,
                          is_valid: e.target.value === '' ? null : e.target.value === 'true'
                        }
                      })}
                    >
                      <MenuItem value="">All Emails</MenuItem>
                      <MenuItem value="true">Valid Emails Only</MenuItem>
                      <MenuItem value="false">Invalid Emails Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                Audience members will be automatically calculated based on your filter criteria
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCreateDialogOpen(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAudience}
              variant="contained"
              disabled={!formData.name}
            >
              Create & Build
            </Button>
          </DialogActions>
        </Dialog>

        {/* Members Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedAudience?.name} - Members Preview
          </DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Added</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewMembers.map((member, index) => (
                    <TableRow key={index}>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.name || '-'}</TableCell>
                      <TableCell>{member.source || '-'}</TableCell>
                      <TableCell>
                        {member.created_at
                          ? new Date(member.created_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{new Date(member.added_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {selectedAudience && selectedAudience.member_count > 10 && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                Showing first 10 of {selectedAudience.member_count} members
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Audience</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete the audience "{audienceToDelete?.name}"?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              This will permanently delete the audience and all {audienceToDelete?.member_count || 0} members.
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Campaigns List Dialog */}
        <Dialog
          open={campaignsDialogOpen}
          onClose={() => setCampaignsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Campaigns Using "{selectedAudience?.name}"
          </DialogTitle>
          <DialogContent>
            {loadingCampaigns ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <Typography>Loading campaigns...</Typography>
              </Box>
            ) : audienceCampaigns.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Recipients</TableCell>
                      <TableCell align="center">Sent</TableCell>
                      <TableCell align="center">Opens</TableCell>
                      <TableCell align="center">Clicks</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Sent At</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audienceCampaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {campaign.subject}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status}
                            size="small"
                            color={
                              campaign.status === 'sent' ? 'success' :
                              campaign.status === 'sending' ? 'warning' :
                              campaign.status === 'draft' ? 'default' :
                              campaign.status === 'failed' ? 'error' : 'primary'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {campaign.total_recipients || 0}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.sent_count || 0}
                          <Typography variant="caption" display="block" color="textSecondary">
                            {campaign.total_recipients > 0
                              ? `${((campaign.sent_count / campaign.total_recipients) * 100).toFixed(0)}%`
                              : '0%'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {campaign.opened_count || 0}
                          <Typography variant="caption" display="block" color="textSecondary">
                            {campaign.sent_count > 0
                              ? `${((campaign.opened_count / campaign.sent_count) * 100).toFixed(1)}%`
                              : '0%'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {campaign.clicked_count || 0}
                          <Typography variant="caption" display="block" color="textSecondary">
                            {campaign.sent_count > 0
                              ? `${((campaign.clicked_count / campaign.sent_count) * 100).toFixed(1)}%`
                              : '0%'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {campaign.started_at
                            ? new Date(campaign.started_at).toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Campaign">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setCampaignsDialogOpen(false);
                                navigate(`/marketing/campaigns/${campaign.id}`);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary" gutterBottom>
                  No campaigns found using this audience
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCampaignsDialogOpen(false);
                    navigate(`/marketing/campaigns/create?audience=${selectedAudience?.id}`);
                  }}
                  sx={{ mt: 2 }}
                >
                  Create First Campaign
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCampaignsDialogOpen(false)}>Close</Button>
            {audienceCampaigns.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCampaignsDialogOpen(false);
                  navigate(`/marketing/campaigns/create?audience=${selectedAudience?.id}`);
                }}
              >
                Create New Campaign
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AudiencesPage;